/**
 * Ticket en ESC/POS, el lenguaje de las impresoras termicas (Epson y
 * compatibles). Es el mismo ticket que sale por el navegador, pero como bytes
 * que se mandan directamente a la impresora: sin dialogo ni impresora
 * predeterminada de por medio.
 */

/** Todo lo que lleva el ticket, ya calculado. Lo produce modeloTicket (TicketPrintService). */
export interface TicketModel {
    centro: { razonSocial: string; nif: string; direccion: string; poblacion: string; telefono: string; email: string };
    titulo: string;
    /** En una devolucion los importes van en NEGATIVO (lineas, suma, base, IVA y total). */
    esDevolucion: boolean;
    fecha: string;
    numero: string;
    sobreTicket: string;
    lineas: { texto: string; importe: number }[];
    suma: number;
    /** Siempre en positivo; el signo lo pone quien lo imprime ("5,00-" en una venta, "5,00" en una devolucion). */
    descuento: number;
    base: number;
    ivaPct: number;
    iva: number;
    total: number;
    exentoIva: boolean;
    pieLegal: string[];
}

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

/**
 * Pagina de codigos CP858 (CP850 con el simbolo del euro): lleva acentos, ñ,
 * ¿ ¡ y €. Solo se listan los caracteres que no son ASCII.
 */
const CP858: Record<string, number> = {
    'Ç': 0x80, 'ü': 0x81, 'é': 0x82, 'â': 0x83, 'ä': 0x84, 'à': 0x85, 'å': 0x86, 'ç': 0x87,
    'ê': 0x88, 'ë': 0x89, 'è': 0x8a, 'ï': 0x8b, 'î': 0x8c, 'ì': 0x8d, 'Ä': 0x8e, 'Å': 0x8f,
    'É': 0x90, 'æ': 0x91, 'Æ': 0x92, 'ô': 0x93, 'ö': 0x94, 'ò': 0x95, 'û': 0x96, 'ù': 0x97,
    'ÿ': 0x98, 'Ö': 0x99, 'Ü': 0x9a, 'ø': 0x9b, '£': 0x9c, 'Ø': 0x9d, '×': 0x9e,
    'á': 0xa0, 'í': 0xa1, 'ó': 0xa2, 'ú': 0xa3, 'ñ': 0xa4, 'Ñ': 0xa5, 'ª': 0xa6, 'º': 0xa7,
    '¿': 0xa8, '®': 0xa9, '¬': 0xaa, '½': 0xab, '¼': 0xac, '¡': 0xad, '«': 0xae, '»': 0xaf,
    'Á': 0xb5, 'Â': 0xb6, 'À': 0xb7, '©': 0xb8, 'ã': 0xc6, 'Ã': 0xc7,
    '€': 0xd5, 'Í': 0xd6, 'Î': 0xd7, 'Ï': 0xd8, 'Ì': 0xde, 'Ó': 0xe0, 'ß': 0xe1, 'Ô': 0xe2,
    'Ò': 0xe3, 'õ': 0xe4, 'Õ': 0xe5, 'µ': 0xe6, 'Ú': 0xe9, 'Û': 0xea, 'Ù': 0xeb,
    'ý': 0xec, 'Ý': 0xed, '´': 0xef, '±': 0xf1, '°': 0xf8
};

/** Numero de pagina de codigos que corresponde a CP858 en Epson (ESC t 19). */
export const DEFAULT_CODE_PAGE = 19;

export interface EscPosOptions {
    /** Caracteres por linea: 48 en un rollo de 80 mm, 32 en uno de 58 mm. */
    columns: number;
    codePage: number;
    /** Sin acentos ni €: para impresoras sin CP858, que sacarian caracteres raros. */
    ascii: boolean;
}

/** Texto a ASCII puro: "ñ" -> "n", "€" -> "EUR"... para impresoras sin paginas de codigos. */
export const toAscii = (text: string): string =>
    text
        .replace(/€/g, 'EUR')
        .replace(/¿/g, '?')
        .replace(/¡/g, '!')
        .replace(/[ªº]/g, 'o')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^\x20-\x7e]/g, '?');

export const encodeText = (text: string, ascii: boolean): number[] => {
    // El espacio duro (U+00A0) no existe en CP858.
    const clean = text.replace(/ /g, ' ');
    const source = ascii ? toAscii(clean) : clean;
    const out: number[] = [];
    for (let i = 0; i < source.length; i++) {
        const ch = source.charAt(i);
        const code = ch.charCodeAt(0);
        if (code >= 0x20 && code < 0x7f) out.push(code);
        else if (CP858[ch] !== undefined) out.push(CP858[ch]);
        else out.push(0x3f); // '?'
    }
    return out;
};

export class EscPosBuilder {
    private bytes: number[] = [];

    constructor(private options: EscPosOptions) {}

    init(): this {
        this.bytes.push(ESC, 0x40);                                                  // ESC @ : reiniciar
        if (!this.options.ascii) this.bytes.push(ESC, 0x74, this.options.codePage);   // ESC t n : pagina de codigos
        return this;
    }

    align(mode: 'left' | 'center' | 'right'): this {
        this.bytes.push(ESC, 0x61, mode === 'left' ? 0 : mode === 'center' ? 1 : 2);
        return this;
    }

    bold(on: boolean): this {
        this.bytes.push(ESC, 0x45, on ? 1 : 0);
        return this;
    }

    /** Tamaño del texto: ancho y alto de 1 a 8 veces. */
    size(width: number, height: number): this {
        this.bytes.push(GS, 0x21, ((Math.max(1, Math.min(8, width)) - 1) << 4) | (Math.max(1, Math.min(8, height)) - 1));
        return this;
    }

    text(value: string): this {
        this.bytes.push.apply(this.bytes, encodeText(value, this.options.ascii));
        return this;
    }

    line(value = ''): this {
        return this.text(value).lf();
    }

    lf(): this {
        this.bytes.push(LF);
        return this;
    }

    feed(lines: number): this {
        this.bytes.push(ESC, 0x64, Math.max(0, Math.min(255, lines)));
        return this;
    }

    /** Corte parcial con avance previo (GS V 66 n). Si la impresora no corta, lo ignora. */
    cut(): this {
        this.bytes.push(GS, 0x56, 0x42, 0x03);
        return this;
    }

    build(): Uint8Array {
        return new Uint8Array(this.bytes);
    }
}

/** Parte `text` en lineas de como mucho `width` caracteres, cortando por palabras cuando se puede. */
export const wrap = (text: string, width: number): string[] => {
    const lines: string[] = [];
    let current = '';
    text.split(/\s+/).filter(Boolean).forEach(word => {
        let rest = word;
        while (rest.length > width) {            // palabra mas larga que la linea: se parte
            if (current) { lines.push(current); current = ''; }
            lines.push(rest.slice(0, width));
            rest = rest.slice(width);
        }
        if (!current) current = rest;
        else if ((current + ' ' + rest).length <= width) current += ' ' + rest;
        else { lines.push(current); current = rest; }
    });
    if (current) lines.push(current);
    return lines.length > 0 ? lines : [''];
};

const padRight = (s: string, n: number) => (s.length >= n ? s : s + ' '.repeat(n - s.length));

/** Izquierda y derecha en la misma linea, con el importe pegado al borde derecho. El texto largo se recorta (como el ticket de papel). */
export const row = (left: string, right: string, columns: number): string => {
    const leftWidth = Math.max(1, columns - right.length - 1);
    const cut = left.length > leftWidth ? left.slice(0, leftWidth) : left;
    return padRight(cut, leftWidth) + ' ' + right;
};

const euros = (n: number): string =>
    new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n).replace(/ /g, ' ');

/** El ticket de venta o de devolucion, con la misma estructura que el de papel. */
export const renderTicketEscPos = (m: TicketModel, options: EscPosOptions): Uint8Array => {
    const cols = options.columns;
    const sep = '-'.repeat(cols);
    const b = new EscPosBuilder(options).init().align('left');

    // Cabecera del centro
    [m.centro.razonSocial, m.centro.nif, m.centro.direccion, m.centro.poblacion, m.centro.telefono, m.centro.email]
        .forEach(l => wrap(l, cols).forEach(x => b.line(x)));
    b.lf();

    // Identificacion
    b.line(m.titulo);
    b.line(`FECHA: ${m.fecha}  Nº ${m.numero}`);
    if (m.sobreTicket) b.line(`Sobre ticket: ${m.sobreTicket}`);
    b.lf();

    // Lineas
    m.lineas.forEach(l => b.line(row(l.texto, euros(l.importe), cols)));
    b.lf();

    // Totales
    b.line(row('Suma:', euros(m.suma), cols));
    if (m.descuento > 0.005) b.line(row('Descuento:', `${euros(m.descuento)}${m.esDevolucion ? '' : '-'}`, cols));
    b.lf();
    b.line(row('Base:', euros(m.base), cols));
    b.line(row(`IVA ${m.ivaPct}%:`, euros(m.iva), cols));
    b.lf();
    b.bold(true).size(1, 2).line(row('TOTAL (euros):', euros(m.total), cols)).size(1, 1).bold(false);
    if (m.exentoIva) b.align('center').line('Operación exenta de IVA').align('left');
    b.lf();

    // Pie
    b.line(sep);
    b.align('center').bold(true).line('*** GRACIAS POR SU VISITA ***').bold(false).lf();
    b.line('www.marcialpons.com').align('left');
    b.line(sep);
    m.pieLegal.forEach(l => wrap(l, cols).forEach(x => b.line(x)));

    b.feed(4).cut();
    return b.build();
};
